import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { CreatePatientInput } from '@datazo/contracts';
import { usePageTitle } from '@/shared/store/page';
import { ApiClientError } from '@/shared/api/client';
import { useCreatePatient } from './api';
import { PatientForm } from './PatientForm';

type Feedback =
  | { kind: 'idle' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; message: string };

const DEFAULT_VALUES: CreatePatientInput = {
  tipoDoc: 'CC',
  documento: '',
  nombres: '',
  apellidos: '',
  fechaNacimiento: '',
  sexo: 'F',
  telefono: '',
  direccion: '',
  ciudad: 'Barranquilla',
  eps: '',
  historiaClinica: '',
  alergias: '',
  contactoEmergencia: '',
};

export function PatientNewPage(): JSX.Element {
  usePageTitle('Nuevo paciente', 'Registro único — datos demográficos básicos');
  const navigate = useNavigate();
  const createPatient = useCreatePatient();
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'idle' });

  const handleSubmit = async (values: CreatePatientInput): Promise<void> => {
    setFeedback({ kind: 'idle' });
    try {
      const created = await createPatient.mutateAsync(values);
      setFeedback({
        kind: 'ok',
        message: 'Paciente creado. Redirigiendo al expediente…',
      });
      setTimeout(() => navigate(`/pacientes/${created.id}`), 600);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401 || err.status === 403) {
          setFeedback({ kind: 'error', message: 'No tienes permiso para crear pacientes.' });
          return;
        }
        if (err.status === 409) {
          setFeedback({
            kind: 'error',
            message: 'Ya existe un paciente con ese tipo y número de documento.',
          });
          return;
        }
        if (err.status >= 400 && err.status < 500) {
          setFeedback({
            kind: 'error',
            message: 'Datos inválidos. Revisa los campos resaltados.',
          });
          return;
        }
      }
      setFeedback({
        kind: 'error',
        message:
          'No fue posible conectar con el servidor. Verifica tu conexión e inténtalo de nuevo.',
      });
    }
  };

  return (
    <div className="max-w-[920px] mx-auto">
      <button
        type="button"
        onClick={() => navigate('/pacientes')}
        className="flex items-center gap-2 text-sm text-text-soft hover:text-text mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a pacientes
      </button>

      <PatientForm
        defaultValues={DEFAULT_VALUES}
        submitLabel="Guardar paciente"
        submittingLabel="Guardando…"
        onSubmit={handleSubmit}
        onCancel={() => navigate('/pacientes')}
        feedback={feedback}
      />
    </div>
  );
}
